/**
 * Lightweight rules-based NER scrubber.
 * Detects names (with honorifics) and street addresses.
 * Not ML-based — uses pattern matching for common formats.
 */

/** Honorifics that precede names */
const HONORIFICS = [
  'Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof', 'Rev', 'Sr', 'Jr',
  'Mr.', 'Mrs.', 'Ms.', 'Miss.', 'Dr.', 'Prof.', 'Rev.', 'Sr.', 'Jr.',
];

/** Street type suffixes */
const STREET_TYPES = [
  'Street', 'St', 'Avenue', 'Ave', 'Boulevard', 'Blvd', 'Drive', 'Dr',
  'Lane', 'Ln', 'Road', 'Rd', 'Court', 'Ct', 'Place', 'Pl',
  'Way', 'Circle', 'Cir', 'Terrace', 'Ter', 'Highway', 'Hwy',
  'Parkway', 'Pkwy', 'Crescent', 'Cres', 'Close', 'Cl',
];

/**
 * Build a regex pattern for honorific + capitalized name(s).
 * Matches: "Mr. John Smith", "Dr. Jane", "Mrs. O'Brien-Smith"
 */
function buildHonorificPattern(): RegExp {
  const honorificGroup = HONORIFICS.map(h => h.replace('.', '\\.')).join('|');
  // Match honorific followed by 1-3 capitalized words (handles hyphenated and apostrophed names)
  return new RegExp(
    `\\b(?:${honorificGroup})\\s+(?:[A-Z][a-zA-Z'-]+(?:\\s+[A-Z][a-zA-Z'-]+){0,2})\\b`,
    'g'
  );
}

/**
 * Build a regex pattern for street addresses.
 * Matches: "123 Main Street", "4567 Oak Ave", "89 Elm Blvd"
 */
function buildAddressPattern(): RegExp {
  const streetGroup = STREET_TYPES.map(s => s.replace('.', '\\.')).join('|');
  // Match number + street name words + street type
  return new RegExp(
    `\\b\\d{1,6}\\s+(?:[A-Z][a-zA-Z]+\\s+){0,3}(?:${streetGroup})\\.?\\b`,
    'g'
  );
}

const HONORIFIC_PATTERN = buildHonorificPattern();
const ADDRESS_PATTERN = buildAddressPattern();

/**
 * Scrub names and addresses from text using rules-based NER.
 */
export function nerScrub(text: string): string {
  let scrubbed = text;

  // Scrub addresses first (more specific pattern)
  ADDRESS_PATTERN.lastIndex = 0;
  scrubbed = scrubbed.replace(ADDRESS_PATTERN, '[ADDRESS]');

  // Scrub honorific-prefixed names
  HONORIFIC_PATTERN.lastIndex = 0;
  scrubbed = scrubbed.replace(HONORIFIC_PATTERN, '[NAME]');

  return scrubbed;
}
